import { IssueState, IssueType } from "@boark/shared/types/issue";
import { IssueCreate } from "@boark/shared/types/issue-create";
import { IssueFastumTicket } from "@boark/shared/types/issue-fastum-ticket";
import { IssueLink } from "@boark/shared/types/issue-link";
import {
  IssueLinkCreate,
  IssueLinkType,
} from "@boark/shared/types/issue-link-create-or-update";
import { IssueUpdate } from "@boark/shared/types/issue-update";
import { IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters } from "@boark/shared/types/issue-with-created-by-and-links-and-children-and-parent-and-registers";
import { formatResidencyName } from "@boark/shared/utils/apartment-residency";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";

import { useGetApartments } from "../queries/get-apartments";
import { useGetAssociation } from "../queries/get-association";
import { useGetAssociationContext } from "../queries/get-association-context";
import { useGetFastumTickets } from "../queries/fastum";
import { useGetHoldings } from "../queries/holding";
import { useGetIssues } from "../queries/issue";
import { useGetLeases } from "../queries/lease";
import { useGetOrganizations } from "../queries/organization";
import { useGetPersons } from "../queries/person";
import { useGetSublets } from "../queries/sublet";
import { formatApartmentLabel } from "../services/apartment";
import {
  formatFastumTicketLabel,
  resolveFastumTicketIdFromTag,
} from "../services/fastum-ticket-label";
import {
  normalizeIssueFormTags,
  uniqueSortedTagSuggestions,
} from "../services/issue";
import { formatOrganizationLabel } from "../services/organization";
import { formatPersonLabel } from "../services/person";
import { useLocale } from "../stores/locale";
import { formatDate } from "../utils/date";
import { getId } from "../utils/id";
import { recordIdToId } from "../utils/record-id-to-id";
import { Button } from "./button";
import { Input } from "./input";
import { SearchableSelect } from "./searchable-select";
import { Select } from "./select";
import { TagInput } from "./tag-input";

export type IssueLinkDirection = "in" | "out";
type IssueLinkRelation = "related" | "blockedBy" | "blocks";

export type IssueFormLink = {
  relationId?: string;
  direction: IssueLinkDirection;
  type: IssueLinkType;
  issueIdentifier: number;
  issueTitle?: string;
};

export type IssueFormRecordType =
  | "apartment"
  | "holding"
  | "lease"
  | "sublet"
  | "person"
  | "organization";

export type IssueFormRecord = {
  type: IssueFormRecordType;
  recordId: string;
  label?: string;
};

export type IssueFormFastumTicket = {
  recordId: string;
  externalId: string;
  title: string;
  status?: string;
};

export type IssueFormValues = {
  title: string;
  description: string;
  tags: string[];
  state: IssueState;
  type: IssueType;
  parentIdentifier?: number;
  links: IssueFormLink[];
  records: IssueFormRecord[];
  fastumTickets: IssueFormFastumTicket[];
};

function parseIssueIdentifier(value: string): number | null {
  const match = value.match(/\d+/);
  const parsed = Number.parseInt(match?.[0] ?? "", 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function hubForBlockGraph(issueIdentifier: number | undefined): number {
  return issueIdentifier ?? 0;
}

function blockEdgesFromFormLinks(
  links: IssueFormLink[],
  issueIdentifier: number | undefined,
): Array<[number, number]> {
  const hub = hubForBlockGraph(issueIdentifier);
  return links
    .filter((link) => link.type === IssueLinkType.BLOCK)
    .map((link) =>
      link.direction === "out"
        ? ([hub, link.issueIdentifier] as [number, number])
        : ([link.issueIdentifier, hub] as [number, number]),
    );
}

function graphHasPathIdentifiers(
  edges: ReadonlyArray<readonly [number, number]>,
  from: number,
  to: number,
): boolean {
  if (from === to) {
    return true;
  }
  const adj = new Map<number, number[]>();
  for (const [a, b] of edges) {
    const list = adj.get(a);
    if (list) {
      list.push(b);
    } else {
      adj.set(a, [b]);
    }
  }
  const stack = [from];
  const seen = new Set<number>();
  while (stack.length > 0) {
    const node = stack.pop();
    if (node === undefined) {
      break;
    }
    if (node === to) {
      return true;
    }
    if (seen.has(node)) {
      continue;
    }
    seen.add(node);
    for (const next of adj.get(node) ?? []) {
      stack.push(next);
    }
  }
  return false;
}

function wouldBlockDraftCreateCycle(
  blockEdges: ReadonlyArray<readonly [number, number]>,
  draftRelation: IssueLinkRelation,
  issueIdentifier: number | undefined,
  targetId: number,
): boolean {
  if (draftRelation === "related") {
    return false;
  }
  const hub = hubForBlockGraph(issueIdentifier);
  const [from, to] =
    draftRelation === "blockedBy" ? [hub, targetId] : [targetId, hub];
  return graphHasPathIdentifiers(blockEdges, to, from);
}

export function issueFormLinkToCreateLink(
  link: IssueFormLink,
): IssueLinkCreate {
  if (link.direction === "in") {
    return { inIdentifier: link.issueIdentifier, type: link.type };
  }

  return { outIdentifier: link.issueIdentifier, type: link.type };
}

export function issueLinksToFormLinks(links: IssueLink[]): IssueFormLink[] {
  return links.map((link) => {
    if ("in" in link) {
      return {
        relationId: link.id,
        direction: "in",
        type: link.type,
        issueIdentifier: link.in.identifier,
        issueTitle: link.in.title,
      };
    }

    return {
      relationId: link.id,
      direction: "out",
      type: link.type,
      issueIdentifier: link.out.identifier,
      issueTitle: link.out.title,
    };
  });
}

function issueFormRecordsToRegisterPayload(records: IssueFormRecord[]): {
  apartments: string[];
  holdings: string[];
  leases: string[];
  sublets: string[];
  persons: string[];
  organizations: string[];
} {
  const apartments: string[] = [];
  const holdings: string[] = [];
  const leases: string[] = [];
  const sublets: string[] = [];
  const persons: string[] = [];
  const organizations: string[] = [];

  for (const record of records) {
    switch (record.type) {
      case "apartment":
        apartments.push(record.recordId);
        break;
      case "holding":
        holdings.push(record.recordId);
        break;
      case "lease":
        leases.push(record.recordId);
        break;
      case "sublet":
        sublets.push(record.recordId);
        break;
      case "person":
        persons.push(record.recordId);
        break;
      case "organization":
        organizations.push(record.recordId);
        break;
    }
  }

  return {
    apartments,
    holdings,
    leases,
    sublets,
    persons,
    organizations,
  };
}

export function issueFastumTicketsToFormFastumTickets(
  fastumTickets: IssueFastumTicket[],
): IssueFormFastumTicket[] {
  return fastumTickets.map((ticket) => ({
    recordId: recordIdToId(ticket.id),
    externalId: ticket.externalId,
    title: ticket.title,
    status: ticket.status,
  }));
}

function issueFormFastumTicketsToPayload(
  fastumTickets: IssueFormFastumTicket[],
): string[] {
  return fastumTickets.map((ticket) => ticket.recordId);
}

export function issueRegistersToFormRecords(
  issue: IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters,
): IssueFormRecord[] {
  return [
    ...issue.apartments.map((apartment) => ({
      type: "apartment" as const,
      recordId: recordIdToId(apartment.id),
      label: formatApartmentLabel(apartment),
    })),
    ...issue.holdings.map((holding) => ({
      type: "holding" as const,
      recordId: recordIdToId(holding.id),
      label: `${formatResidencyName(holding)} (${formatDate(holding.fromDate)})`,
    })),
    ...issue.leases.map((lease) => ({
      type: "lease" as const,
      recordId: recordIdToId(lease.id),
      label: `${formatResidencyName(lease)} (${formatDate(lease.fromDate)})`,
    })),
    ...issue.sublets.map((sublet) => ({
      type: "sublet" as const,
      recordId: recordIdToId(sublet.id),
      label: `${formatResidencyName(sublet)} (${formatDate(sublet.fromDate)})`,
    })),
    ...issue.persons.map((person) => ({
      type: "person" as const,
      recordId: recordIdToId(person.id),
      label: formatPersonLabel({
        ...person,
        holdings: [],
        leases: [],
        sublets: [],
      }),
    })),
    ...issue.organizations.map((organization) => ({
      type: "organization" as const,
      recordId: recordIdToId(organization.id),
      label: formatOrganizationLabel(organization),
    })),
  ];
}

export function issueFormValuesToCreate(
  values: IssueFormValues,
  options?: { includeFastumTickets?: boolean },
): IssueCreate {
  const createPayloadLinks = values.links.map((link) =>
    issueFormLinkToCreateLink(link),
  );
  const { apartments, holdings, leases, sublets, persons, organizations } =
    issueFormRecordsToRegisterPayload(values.records);
  const fastumTickets = issueFormFastumTicketsToPayload(values.fastumTickets);

  return {
    title: values.title.trim(),
    ...(values.description.trim() !== ""
      ? { description: values.description.trim() }
      : {}),
    tags: normalizeIssueFormTags(values.tags),
    state: values.state,
    type: values.type,
    ...(values.parentIdentifier
      ? { parentIdentifier: values.parentIdentifier }
      : {}),
    ...(createPayloadLinks.length > 0 ? { links: createPayloadLinks } : {}),
    ...(apartments.length > 0 ? { apartments } : {}),
    ...(holdings.length > 0 ? { holdings } : {}),
    ...(leases.length > 0 ? { leases } : {}),
    ...(sublets.length > 0 ? { sublets } : {}),
    ...(persons.length > 0 ? { persons } : {}),
    ...(organizations.length > 0 ? { organizations } : {}),
    ...(options?.includeFastumTickets && fastumTickets.length > 0
      ? { fastumTickets }
      : {}),
  };
}

export function issueFormValuesToUpdate(
  values: IssueFormValues,
  options?: { includeFastumTickets?: boolean },
): IssueUpdate {
  const registers = issueFormRecordsToRegisterPayload(values.records);
  const fastumTickets = issueFormFastumTicketsToPayload(values.fastumTickets);

  return {
    title: values.title.trim(),
    description:
      values.description.trim() === "" ? null : values.description.trim(),
    tags: normalizeIssueFormTags(values.tags),
    state: values.state,
    parentIdentifier: values.parentIdentifier ?? null,
    apartments: registers.apartments,
    holdings: registers.holdings,
    leases: registers.leases,
    sublets: registers.sublets,
    persons: registers.persons,
    organizations: registers.organizations,
    ...(options?.includeFastumTickets
      ? { fastumTickets }
      : {}),
  };
}

export function IssueForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
  issueIdentifier,
  onAddLink,
  onAddLinks,
  onRemoveLink,
  typeDisabled = false,
  parentDisabled = false,
}: {
  onCancel: () => void;
  onChange: (values: IssueFormValues) => void;
  onSubmit: () => void;
  values: IssueFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  issueIdentifier?: number;
  onAddLink?: (link: IssueFormLink) => Promise<void> | void;
  onAddLinks?: (links: IssueFormLink[]) => Promise<void> | void;
  onRemoveLink?: (link: IssueFormLink) => Promise<void> | void;
  typeDisabled?: boolean;
  parentDisabled?: boolean;
}) {
  const { locale: t, get: getLocale } = useLocale();
  const { data: associationContext } = useGetAssociationContext();
  const { data: association } = useGetAssociation(
    getId(associationContext?.id ?? ""),
  );
  const hasFastumIntegration = Boolean(association?.integrations?.fastum);
  const { data: issues } = useGetIssues();
  const { data: fastumTickets } = useGetFastumTickets({
    enabled: hasFastumIntegration,
  });
  const { data: apartments } = useGetApartments();
  const { data: holdings } = useGetHoldings();
  const { data: leases } = useGetLeases();
  const { data: sublets } = useGetSublets();
  const { data: persons } = useGetPersons();
  const { data: organizations } = useGetOrganizations();
  const [draftRelation, setDraftRelation] =
    useState<IssueLinkRelation>("related");
  const [draftTags, setDraftTags] = useState<string[]>([]);
  const [draftRecordType, setDraftRecordType] =
    useState<IssueFormRecordType>("apartment");
  const [draftRecordId, setDraftRecordId] = useState("");
  const [draftFastumTags, setDraftFastumTags] = useState<string[]>([]);

  const isValid = useMemo(() => values.title.trim() !== "", [values.title]);

  const isDisabled = useMemo(() => disabled || loading, [disabled, loading]);

  const tagSuggestions = useMemo(
    () => uniqueSortedTagSuggestions(issues),
    [issues],
  );

  const stateOptions = useMemo(
    () => [
      { value: IssueState.TODO, label: t.issue.stateTodo },
      { value: IssueState.IN_PROGRESS, label: t.issue.stateInProgress },
      { value: IssueState.DONE, label: t.issue.stateDone },
      { value: IssueState.WONTDO, label: t.issue.stateWontdo },
    ],
    [t.issue],
  );

  const typeOptions = useMemo(
    () => [
      { value: IssueType.GROUP, label: t.issue.typeGroup },
      { value: IssueType.ISSUE, label: t.issue.typeIssue },
      { value: IssueType.TASK, label: t.issue.typeTask },
    ],
    [t.issue],
  );

  const expectedParentType = useMemo(() => {
    switch (values.type) {
      case IssueType.GROUP:
        return undefined;
      case IssueType.ISSUE:
        return IssueType.GROUP;
      case IssueType.TASK:
        return IssueType.ISSUE;
    }
  }, [values.type]);

  const parentOptions = useMemo(() => {
    if (!expectedParentType) {
      return [];
    }
    return (issues ?? [])
      .filter((issue) => issue.type === expectedParentType)
      .map((issue) => ({
        value: String(issue.identifier),
        label: `${issue.title} (#${issue.identifier})`,
      }));
  }, [issues, expectedParentType]);

  const hasSelectedParent = useMemo(
    () =>
      values.parentIdentifier !== undefined &&
      parentOptions.some(
        (option) => Number(option.value) === values.parentIdentifier,
      ),
    [values.parentIdentifier, parentOptions],
  );

  const fastumTicketAutocompleteOptions = useMemo(() => {
    const linkedIds = new Set(
      values.fastumTickets.map((ticket) => ticket.recordId),
    );
    return (fastumTickets ?? [])
      .filter((ticket) => !linkedIds.has(recordIdToId(ticket.id)))
      .map((ticket) => ({
        value: recordIdToId(ticket.id),
        label: formatFastumTicketLabel(ticket),
      }));
  }, [fastumTickets, values.fastumTickets]);

  const linkAutocompleteOptions = useMemo(() => {
    const linkedIdentifiers = new Set(
      values.links.map((link) => link.issueIdentifier),
    );
    const blockEdges = blockEdgesFromFormLinks(values.links, issueIdentifier);
    return (issues ?? [])
      .filter((issue) => {
        if (
          issueIdentifier !== undefined &&
          issue.identifier === issueIdentifier
        ) {
          return false;
        }
        if (linkedIdentifiers.has(issue.identifier)) {
          return false;
        }
        if (
          wouldBlockDraftCreateCycle(
            blockEdges,
            draftRelation,
            issueIdentifier,
            issue.identifier,
          )
        ) {
          return false;
        }
        return true;
      })
      .map((issue) => ({
        value: String(issue.identifier),
        label: `${issue.title} (#${issue.identifier})`,
      }));
  }, [issues, issueIdentifier, values.links, draftRelation]);

  const issueByIdentifier = useMemo(
    () =>
      new Map(
        (issues ?? []).map((issue) => [issue.identifier, issue] as const),
      ),
    [issues],
  );

  const relationOptions = useMemo(
    () => [
      { value: "related", label: t.issue.linkRelatedTo },
      { value: "blockedBy", label: t.issue.linkBlockedBy },
      { value: "blocks", label: t.issue.linkBlocks },
    ],
    [t.issue],
  );

  const recordTypeOptions = useMemo(
    () => [
      { value: "apartment", label: t.apartment.apartment },
      { value: "holding", label: t.holding.holding },
      { value: "lease", label: t.lease.lease },
      { value: "sublet", label: t.sublet.sublet },
      { value: "person", label: t.person.person },
      { value: "organization", label: t.organization.organization },
    ],
    [t.apartment, t.holding, t.lease, t.sublet, t.person, t.organization],
  );

  const linkedRecordIds = useMemo(
    () =>
      new Set(
        values.records.map((record) => `${record.type}:${record.recordId}`),
      ),
    [values.records],
  );

  const draftRecordOptions = useMemo(() => {
    const isAvailable = (type: IssueFormRecordType, recordId: string) =>
      !linkedRecordIds.has(`${type}:${recordIdToId(recordId)}`);

    switch (draftRecordType) {
      case "apartment":
        return (apartments ?? [])
          .filter((apartment) => isAvailable("apartment", apartment.id))
          .map((apartment) => ({
            value: recordIdToId(apartment.id),
            label: formatApartmentLabel(apartment),
          }));
      case "holding":
        return (holdings ?? [])
          .filter((holding) => isAvailable("holding", holding.id))
          .map((holding) => ({
            value: recordIdToId(holding.id),
            label: `${formatApartmentLabel(holding.apartment)} — ${formatResidencyName(holding)} (${formatDate(holding.fromDate)})`,
          }));
      case "lease":
        return (leases ?? [])
          .filter((lease) => isAvailable("lease", lease.id))
          .map((lease) => ({
            value: recordIdToId(lease.id),
            label: `${formatApartmentLabel(lease.apartment)} — ${formatResidencyName(lease)} (${formatDate(lease.fromDate)})`,
          }));
      case "sublet":
        return (sublets ?? [])
          .filter((sublet) => isAvailable("sublet", sublet.id))
          .map((sublet) => {
            const apartment =
              "holding" in sublet
                ? sublet.holding.apartment
                : sublet.lease.apartment;
            return {
              value: recordIdToId(sublet.id),
              label: `${formatApartmentLabel(apartment)} — ${formatResidencyName(sublet)} (${formatDate(sublet.fromDate)})`,
            };
          });
      case "person":
        return (persons ?? [])
          .filter((person) => isAvailable("person", person.id))
          .map((person) => ({
            value: recordIdToId(person.id),
            label: formatPersonLabel(person),
          }));
      case "organization":
        return (organizations ?? [])
          .filter((organization) =>
            isAvailable("organization", organization.id),
          )
          .map((organization) => ({
            value: recordIdToId(organization.id),
            label: formatOrganizationLabel(organization),
          }));
    }
  }, [
    apartments,
    draftRecordType,
    holdings,
    leases,
    linkedRecordIds,
    organizations,
    persons,
    sublets,
  ]);

  const recordTypeLabel = (type: IssueFormRecordType): string => {
    switch (type) {
      case "apartment":
        return t.apartment.apartment;
      case "holding":
        return t.holding.holding;
      case "lease":
        return t.lease.lease;
      case "sublet":
        return t.sublet.sublet;
      case "person":
        return t.person.person;
      case "organization":
        return t.organization.organization;
    }
  };

  const linkVerb = (link: IssueFormLink): string => {
    if (link.type === IssueLinkType.RELATE) {
      return t.issue.linkRelatedTo;
    }
    return link.direction === "out"
      ? t.issue.linkBlockedBy
      : t.issue.linkBlocks;
  };

  const relationOfLink = (link: IssueFormLink): IssueLinkRelation => {
    if (link.type === IssueLinkType.RELATE) {
      return "related";
    }
    return link.direction === "out" ? "blockedBy" : "blocks";
  };

  const buildLinkFromRelation = (
    relation: IssueLinkRelation,
    targetIdentifier: number,
    existing?: IssueFormLink,
  ): IssueFormLink => {
    const issue = issueByIdentifier.get(targetIdentifier);
    if (relation === "related") {
      return {
        relationId: existing?.relationId,
        direction: "out",
        type: IssueLinkType.RELATE,
        issueIdentifier: targetIdentifier,
        issueTitle: issue?.title ?? existing?.issueTitle,
      };
    }
    if (relation === "blockedBy") {
      return {
        relationId: existing?.relationId,
        direction: "out",
        type: IssueLinkType.BLOCK,
        issueIdentifier: targetIdentifier,
        issueTitle: issue?.title ?? existing?.issueTitle,
      };
    }
    return {
      relationId: existing?.relationId,
      direction: "in",
      type: IssueLinkType.BLOCK,
      issueIdentifier: targetIdentifier,
      issueTitle: issue?.title ?? existing?.issueTitle,
    };
  };

  const addDraftLinks = async () => {
    const draftIdentifiers = Array.from(
      new Set(
        draftTags
          .map((tag) => parseIssueIdentifier(tag))
          .filter((id): id is number => id !== null)
          .filter((id) => (issueIdentifier ? id !== issueIdentifier : true)),
      ),
    );

    if (draftIdentifiers.length === 0) {
      return;
    }

    const linkedIdentifiers = new Set(
      values.links.map((link) => link.issueIdentifier),
    );
    const blockEdges = blockEdgesFromFormLinks(values.links, issueIdentifier);
    const identifiersToAdd = draftIdentifiers.filter((identifier) => {
      if (linkedIdentifiers.has(identifier)) {
        return false;
      }
      if (
        wouldBlockDraftCreateCycle(
          blockEdges,
          draftRelation,
          issueIdentifier,
          identifier,
        )
      ) {
        return false;
      }
      return true;
    });

    if (identifiersToAdd.length === 0) {
      setDraftTags([]);
      return;
    }

    if (onAddLinks || onAddLink || onRemoveLink) {
      if (onAddLinks) {
        await onAddLinks(
          identifiersToAdd.map((identifier) =>
            buildLinkFromRelation(draftRelation, identifier),
          ),
        );
      } else {
        for (const identifier of identifiersToAdd) {
          if (onAddLink) {
            await onAddLink(buildLinkFromRelation(draftRelation, identifier));
          }
        }
      }
      setDraftTags([]);
      return;
    }

    const currentRelationLinks = values.links.filter(
      (link) => relationOfLink(link) === draftRelation,
    );
    const currentByIdentifier = new Map(
      currentRelationLinks.map((link) => [link.issueIdentifier, link] as const),
    );
    const nextLinks = identifiersToAdd.map((identifier) =>
      buildLinkFromRelation(
        draftRelation,
        identifier,
        currentByIdentifier.get(identifier),
      ),
    );
    onChange({
      ...values,
      links: [...values.links, ...nextLinks],
    });
    setDraftTags([]);
  };

  const removeLink = async (index: number) => {
    const link = values.links[index];
    if (!link) {
      return;
    }

    if (onRemoveLink) {
      await onRemoveLink(link);
      return;
    }

    onChange({
      ...values,
      links: values.links.filter((_, linkIndex) => linkIndex !== index),
    });
  };

  const addDraftRecord = () => {
    if (draftRecordId === "") {
      return;
    }

    if (linkedRecordIds.has(`${draftRecordType}:${draftRecordId}`)) {
      return;
    }

    const selectedOption = draftRecordOptions.find(
      (option) => option.value === draftRecordId,
    );

    onChange({
      ...values,
      records: [
        ...values.records,
        {
          type: draftRecordType,
          recordId: draftRecordId,
          label: selectedOption?.label,
        },
      ],
    });
    setDraftRecordId("");
  };

  const removeRecord = (index: number) => {
    onChange({
      ...values,
      records: values.records.filter((_, recordIndex) => recordIndex !== index),
    });
  };

  const addDraftFastumTickets = () => {
    if (!fastumTickets?.length) {
      return;
    }

    const linkedIds = new Set(
      values.fastumTickets.map((ticket) => ticket.recordId),
    );
    const ticketsToAdd = draftFastumTags
      .map((tag) => resolveFastumTicketIdFromTag(tag, fastumTickets))
      .filter((recordId): recordId is string => recordId !== null)
      .filter((recordId) => !linkedIds.has(recordId))
      .map((recordId) => {
        const ticket = fastumTickets.find(
          (item) => recordIdToId(item.id) === recordId,
        );
        if (!ticket) {
          return null;
        }
        return {
          recordId,
          externalId: ticket.externalId,
          title: ticket.title,
          status: ticket.status,
        };
      })
      .filter((ticket): ticket is IssueFormFastumTicket => ticket !== null);

    if (ticketsToAdd.length === 0) {
      setDraftFastumTags([]);
      return;
    }

    onChange({
      ...values,
      fastumTickets: [...values.fastumTickets, ...ticketsToAdd],
    });
    setDraftFastumTags([]);
  };

  const removeFastumTicket = (index: number) => {
    onChange({
      ...values,
      fastumTickets: values.fastumTickets.filter(
        (_, ticketIndex) => ticketIndex !== index,
      ),
    });
  };

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Input
          required
          className="col-span-2"
          disabled={isDisabled}
          label={t.issue.title}
          type="text"
          value={values.title}
          onChange={(e) => onChange({ ...values, title: e.target.value })}
        />
        <div className="col-span-2">
          <label
            className={`block text-sm font-medium ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {t.issue.description}
          </label>
          <textarea
            className="w-full border-1 border-gray-300 rounded-xs p-2 min-h-[4.5rem] bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            disabled={isDisabled}
            value={values.description}
            onChange={(e) =>
              onChange({ ...values, description: e.target.value })
            }
          />
        </div>
        <div className="col-span-2 w-full">
          <TagInput
            disabled={isDisabled}
            hint={t.issue.tagsHint}
            label={t.issue.tags}
            placeholder={t.issue.tagsPlaceholder}
            suggestions={tagSuggestions}
            value={values.tags}
            onChange={(tags) => onChange({ ...values, tags })}
          />
        </div>
        <div className="col-span-2">
          <Select
            disabled={isDisabled || typeDisabled}
            label={t.issue.type}
            options={typeOptions}
            value={values.type}
            onChange={(type) => {
              const nextType = type as IssueType;
              const nextExpectedParentType =
                nextType === IssueType.TASK
                  ? IssueType.ISSUE
                  : nextType === IssueType.ISSUE
                    ? IssueType.GROUP
                    : undefined;
              const nextParentIdentifier =
                nextExpectedParentType &&
                (issues ?? []).some(
                  (issue) =>
                    issue.identifier === values.parentIdentifier &&
                    issue.type === nextExpectedParentType,
                )
                  ? values.parentIdentifier
                  : undefined;
              onChange({
                ...values,
                type: nextType,
                parentIdentifier: nextParentIdentifier,
              });
            }}
          />
        </div>
        <div className="col-span-2">
          <Select
            allowEmpty
            disabled={isDisabled || !expectedParentType || parentDisabled}
            label={t.issue.parent}
            options={parentOptions}
            placeholder={t.issue.parentEmpty}
            value={
              hasSelectedParent ? String(values.parentIdentifier ?? "") : ""
            }
            onChange={(parentId) =>
              onChange({
                ...values,
                parentIdentifier:
                  parentId === "" ? undefined : Number.parseInt(parentId, 10),
              })
            }
          />
        </div>
        <div className="col-span-2">
          <Select
            disabled={isDisabled}
            label={t.issue.state}
            options={stateOptions}
            value={values.state}
            onChange={(state) =>
              onChange({ ...values, state: state as IssueState })
            }
          />
        </div>
        <div className="col-span-2 border-t border-gray-200 mt-2 pt-3">
          <label
            className={`block text-sm font-medium ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {t.issue.links}
          </label>

          <div className="mt-2 border-1 border-gray-300 rounded-xs p-2 bg-white disabled:bg-gray-100">
            {values.links.length === 0 && (
              <p className="text-sm text-gray-500">{t.issue.linksEmpty}</p>
            )}
            {values.links.map((link, index) => (
              <div
                key={
                  link.relationId ??
                  `${link.direction}-${link.type}-${link.issueIdentifier}-${index}`
                }
                className="flex items-center justify-between gap-2 py-1"
              >
                <p className="text-sm text-gray-700 truncate">
                  <span className="font-medium">{linkVerb(link)}</span>{" "}
                  {link.issueTitle ?? `#${link.issueIdentifier}`}
                </p>
                <Button
                  disabled={isDisabled}
                  severity="secondary"
                  size="sm"
                  type="button"
                  onClick={() => {
                    void removeLink(index);
                  }}
                >
                  {t.issue.linkRemove}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-2 grid grid-cols-[12rem_1fr_auto] gap-2 items-end">
            <Select
              disabled={isDisabled}
              label={t.issue.linkRelation}
              options={relationOptions}
              value={draftRelation}
              onChange={(relation) =>
                setDraftRelation(relation as IssueLinkRelation)
              }
            />
            <TagInput
              disabled={isDisabled}
              label={t.issue.linkTarget}
              placeholder={t.issue.linkTargetManualPlaceholder}
              suggestions={linkAutocompleteOptions.map((issue) => issue.label)}
              value={draftTags}
              onChange={setDraftTags}
            />
            <Button
              disabled={isDisabled || draftTags.length === 0}
              size="sm"
              type="button"
              onClick={() => {
                void addDraftLinks();
              }}
            >
              {t.issue.linkAdd}
            </Button>
          </div>
        </div>
        <div className="col-span-2 border-t border-gray-200 mt-2 pt-3">
          <label
            className={`block text-sm font-medium ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {t.issue.registers}
          </label>

          <div className="mt-2 border-1 border-gray-300 rounded-xs p-2 bg-white disabled:bg-gray-100">
            {values.records.length === 0 && (
              <p className="text-sm text-gray-500">{t.issue.registersEmpty}</p>
            )}
            {values.records.map((record, index) => (
              <div
                key={`${record.type}-${record.recordId}-${index}`}
                className="flex items-center justify-between gap-2 py-1"
              >
                <p className="text-sm text-gray-700 truncate">
                  <span className="font-medium">
                    {recordTypeLabel(record.type)}
                  </span>{" "}
                  {record.label ?? record.recordId}
                </p>
                <Button
                  disabled={isDisabled}
                  severity="secondary"
                  size="sm"
                  type="button"
                  onClick={() => {
                    removeRecord(index);
                  }}
                >
                  {t.issue.linkRemove}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-2 grid grid-cols-[12rem_1fr_auto] gap-2 items-end">
            <Select
              disabled={isDisabled}
              label={t.issue.recordType}
              options={recordTypeOptions}
              value={draftRecordType}
              onChange={(type) => {
                setDraftRecordType(type as IssueFormRecordType);
                setDraftRecordId("");
              }}
            />
            <SearchableSelect
              disabled={isDisabled}
              label={t.issue.recordTarget}
              options={draftRecordOptions}
              placeholder={t.select.placeholder}
              value={draftRecordId}
              onChange={setDraftRecordId}
            />
            <Button
              disabled={isDisabled || draftRecordId === ""}
              size="sm"
              type="button"
              onClick={addDraftRecord}
            >
              {t.issue.recordAdd}
            </Button>
          </div>
        </div>
        {hasFastumIntegration && (
          <div className="col-span-2 border-t border-gray-200 mt-2 pt-3">
            <label
              className={`block text-sm font-medium ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
            >
              {t.issue.fastumTicketsLinked}
            </label>

            <div className="mt-2 border-1 border-gray-300 rounded-xs p-2 bg-white disabled:bg-gray-100">
              {values.fastumTickets.length === 0 && (
                <p className="text-sm text-gray-500">
                  {t.issue.fastumTicketsLinkedEmpty}
                </p>
              )}
              {values.fastumTickets.map((ticket, index) => (
                <div
                  key={`${ticket.recordId}-${index}`}
                  className="flex items-center justify-between gap-2 py-1"
                >
                  <p className="text-sm text-gray-700 truncate">
                    <span className="font-medium">{ticket.externalId}</span>{" "}
                    {ticket.title}
                    {ticket.status ? ` (${ticket.status})` : ""}
                  </p>
                  <Button
                    disabled={isDisabled}
                    severity="secondary"
                    size="sm"
                    type="button"
                    onClick={() => {
                      removeFastumTicket(index);
                    }}
                  >
                    {t.issue.linkRemove}
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 items-end">
              <TagInput
                disabled={isDisabled}
                label={t.issue.fastumTicketTarget}
                placeholder={t.issue.fastumTicketTargetPlaceholder}
                suggestions={fastumTicketAutocompleteOptions.map(
                  (ticket) => ticket.label,
                )}
                value={draftFastumTags}
                onChange={setDraftFastumTags}
              />
              <Button
                disabled={isDisabled || draftFastumTags.length === 0}
                size="sm"
                type="button"
                onClick={addDraftFastumTickets}
              >
                {t.issue.fastumTicketAdd}
              </Button>
            </div>
          </div>
        )}
      </div>
      {hasError && (
        <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          {t.issue.error}
        </div>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <Button disabled={isDisabled} severity="secondary" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button
          disabled={isDisabled || !isValid}
          loading={loading}
          type="submit"
        >
          {getLocale("page.save")}
        </Button>
      </div>
    </form>
  );
}
